import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import ReusableButton from './Button/Button';
import CrossIcon from '../../assets/CrossIcon';
import CheckIcon from '../../assets/CheckIcon';
import RedCrossIcon from '../../assets/icon/RedCrossIcon';
import { translate } from '../../i18n';
import PaxPercentageBarSection from '../ui/PaxPercentageBarSection';
import { EditFlightForm } from '../../type/editFlightForm';
import { formatCurrentTime } from '../../utils/dateUtils';
import operationAPI from '../../api/operationApi';
import { toast } from 'react-toastify';
import useBgColorStore from '../../store/bgColor';
import CrossIconDarkForm from '../../assets/icon/CrossIconForm';

interface EditFlightFormProps {
  title?: string;
  balloonName?: string;
  capacity?: number;
  allocatedPax?: number;
  launchTime?: string | undefined;
  landTime?: string | undefined;
  fuel?: number;
  closeModal: () => void;
  onConfirm: (data: EditFlightForm) => void;
  operationId: number;
  operationBalloonId: number;
  refreshTimeValue?: () => void;
  onAfterSubmit?: () => void;
}

const EditFormFlight: React.FC<EditFlightFormProps> = ({
  title,
  balloonName,
  capacity,
  allocatedPax,
  launchTime,
  landTime,
  fuel,
  closeModal,
  operationId,
  operationBalloonId,
  onAfterSubmit,
}) => {
  const { register, handleSubmit } = useForm<EditFlightForm>({
    defaultValues: {
      launchTime: launchTime,
      landingTime: landTime,
      fuelUsed: fuel ?? 0,
      operationId,
      operationBalloonId,
    },
  });
  const { bgColor, updateBgColor } = useBgColorStore();
  const onSubmit: SubmitHandler<EditFlightForm> = async (data) => {
    const formattedLandTime = formatCurrentTime(data?.landingTime);
    const formattedLaunchTime = formatCurrentTime(data?.launchTime);
    try {
      const response = await operationAPI.updateFlight({
        landingTime: formattedLandTime,
        launchTime: formattedLaunchTime,
        fuelUsed: data.fuelUsed,
        operationBalloonId: data.operationBalloonId,
        operationId: data.operationId,
      });
      toast.success(translate('operation.flightDetailsUpdatedSuccessfully'));
      if (onAfterSubmit) {
        onAfterSubmit();
      }
      closeModal();
    } catch (error) {
      toast.success(
        translate('operation.flightDetailsUpdatedSuccessfullyNotUpdated'),
      );
    }
  };

  return (
    <div
      className={`w-auto mx-auto ${bgColor} p-6 rounded-lg shadow-md relative`}
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-bold text-textColor">{title}</h2>
        <button onClick={closeModal}>
          <RedCrossIcon />
        </button>
      </div>
      <div className="border-b-2 border-[#EEF7FF] mb-4"></div>
      <div className="">
        <div className="text-left text-lg font-semibold mb-2 text-text-primary">
          {balloonName}
        </div>
        <PaxPercentageBarSection
          allocatedPax={allocatedPax ?? 0}
          capacity={capacity ?? 0}
        />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4 p-2">
        <div className="flex justify-between gap-4">
          <div>
            <label className="block text-sm font-normal text-text-primary text-start">
              {translate('operation.launchTime')}
            </label>
            <input
              type="time"
              {...register('launchTime')}
              className="mt-1 block p-2  border border-customBorder-border rounded-md bg-gray-100 w-[160px] md:w-[255px]  bg-backgroundColor-darkThemeColor text-text-primary"
            />
          </div>
          <div>
            <label className="block text-sm font-normal text-text-primary text-start">
              {translate('operation.landTime')}
            </label>
            <input
              type="time"
              {...register('landingTime')}
              className="mt-1 block p-2 border border-customBorder-border w-[160px] md:w-[255px] rounded-md bg-gray-100  bg-backgroundColor-darkThemeColor text-text-primary"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-normal text-text-primary text-start">
              {translate('operation.fuel')}
            </label>
            <input
              type="number"
              {...register('fuelUsed', {
                valueAsNumber: true,
                required: true,
                validate: (value) => !isNaN(value) || 'Must be a number',
              })}
              className="mt-1 block p-2 border border-customBorder-border rounded-md bg-gray-100 w-[160px] md:w-[255px]  bg-backgroundColor-darkThemeColor text-text-primary"
            />
          </div>
        </div>

        <input type="hidden" {...register('operationId')} />
        <input type="hidden" {...register('operationBalloonId')} />

        <div className="flex justify-end">
          <div>
            <ReusableButton
              onClick={closeModal}
              variant="secondary"
              children={translate('common.cancel')}
              icon={
                  <div>
                    <div className="block dark:hidden">
                      <CrossIcon />
                    </div>
                    <div className="hidden dark:block">
                      <CrossIconDarkForm />
                    </div>
                  </div>
                }
              className="mt-1 block p-2 border border-[#004E9B] rounded-md bg-gray-100 w-[144px]  bg-button-cancelButtonBgColor text-text-cancelButtonTextColor"
            />
          </div>
          <div>
            <ReusableButton
              icon={<CheckIcon />}
              children={translate('common.confirm')}
              variant="primary"
              className="mt-1 block p-2 border border-[#004E9B] rounded-md bg-gray-100 w-[144px]  bg-background-backgroundColor"
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditFormFlight;
